import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/format";
import { BOOKING_STATUS } from "@/lib/bookingConstants";
import { PaymentBadge } from "@/components/dashboard/StatusBadge";
import BookingFormDrawer from "@/components/reservations/BookingFormDrawer";
import BookingDetailsDrawer from "@/components/reservations/BookingDetailsDrawer";
import { FieldSelect } from "@/components/ui/field-select";
import { ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react";

const HOURS = Array.from({ length: 16 }, (_, i) => 7 + i); // 07:00 -> 22:00
const DAYS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function addDays(iso, n) { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); }
function fmtFr(iso) { return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }); }

export default function Planning() {
  const { orgId, can } = useAuth();
  const [view, setView] = useState("day");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [activity, setActivity] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [employee, setEmployee] = useState("");
  const [status, setStatus] = useState("");
  const [detailsId, setDetailsId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const { data: acts } = useQuery({ queryKey: ["activities", orgId], queryFn: async () => (await api.get("/api/activities", { params: { organization_id: orgId } })).data, enabled: !!orgId });
  const { data: vehs } = useQuery({ queryKey: ["vehicles", orgId], queryFn: async () => (await api.get("/api/vehicles", { params: { organization_id: orgId } })).data, enabled: !!orgId });
  const { data: emps } = useQuery({ queryKey: ["employees", orgId], queryFn: async () => (await api.get("/api/employees", { params: { organization_id: orgId } })).data, enabled: !!orgId });

  const { data, isLoading } = useQuery({
    queryKey: ["planning", orgId, view, date, activity, vehicle, employee, status],
    queryFn: async () => (await api.get("/api/planning", {
      params: { organization_id: orgId, view, date, activity_id: activity || undefined,
        vehicle_id: vehicle || undefined, employee_id: employee || undefined, status: status || undefined },
    })).data,
    enabled: !!orgId,
  });

  const step = view === "week" ? 7 : 1;
  const bookings = (data?.bookings || []).filter((b) => !["CANCELLED", "NO_SHOW"].includes(b.status));
  const summary = data?.summary;
  const openCreate = (d, t) => { setPrefill({ date: d, start_time: t }); setFormOpen(true); };

  return (
    <div className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Planning</h1>
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 rounded-lg bg-slate-100 p-0.5">
            {["day", "week"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`planning-view-${v}`}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                {v === "day" ? "Jour" : "Semaine"}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setDate((d) => addDays(d, -step))} data-testid="planning-prev" className="rounded-lg border border-slate-200 p-1.5 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setDate(new Date().toISOString().slice(0, 10))} data-testid="planning-today" className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium hover:bg-slate-50">Aujourd'hui</button>
            <button onClick={() => setDate((d) => addDays(d, step))} data-testid="planning-next" className="rounded-lg border border-slate-200 p-1.5 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-wrap gap-2">
        <FieldSelect testId="planning-filter-activity" className="w-44" value={activity || "ALL"} onChange={(v) => setActivity(v === "ALL" ? "" : v)}
          options={[{ value: "ALL", label: "Toute activité" }, ...(acts?.activities || []).map((a) => ({ value: a.id, label: a.name }))]} />
        <FieldSelect testId="planning-filter-vehicle" className="w-44" value={vehicle || "ALL"} onChange={(v) => setVehicle(v === "ALL" ? "" : v)}
          options={[{ value: "ALL", label: "Tout véhicule" }, ...(vehs?.vehicles || []).map((v) => ({ value: v.id, label: v.code }))]} />
        <FieldSelect testId="planning-filter-employee" className="w-44" value={employee || "ALL"} onChange={(v) => setEmployee(v === "ALL" ? "" : v)}
          options={[{ value: "ALL", label: "Tout guide" }, ...(emps?.employees || []).map((e) => ({ value: e.id, label: e.name }))]} />
        <FieldSelect testId="planning-filter-status" className="w-44" value={status || "ALL"} onChange={(v) => setStatus(v === "ALL" ? "" : v)}
          options={[{ value: "ALL", label: "Tout statut" }, ...Object.entries(BOOKING_STATUS).map(([k, v]) => ({ value: k, label: v.label }))]} />
      </div>

      {/* Day summary */}
      {view === "day" && summary && (
        <div className="mt-4 grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-4 lg:grid-cols-6" data-testid="planning-summary">
          <Stat label="Réservations" value={summary.total} />
          <Stat label="Participants" value={summary.participants} />
          <Stat label="Terminées" value={summary.completed} />
          <Stat label="En cours" value={summary.in_progress} />
          <Stat label="À encaisser" value={summary.to_collect_count} />
          <Stat label="Montant à encaisser" value={formatMoney(summary.to_collect_cents, summary.currency)} mono />
        </div>
      )}

      <p className="mt-4 text-sm font-medium capitalize text-slate-600">{view === "day" ? fmtFr(date) : `Semaine du ${fmtFr(data?.start || date)}`}</p>

      {isLoading ? (
        <div className="mt-4 flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : view === "day" ? (
        <DayGrid date={date} bookings={bookings} onOpen={setDetailsId} onSlot={openCreate} canCreate={can("bookings.create")} />
      ) : (
        <WeekGrid start={data?.start || date} bookings={bookings} onOpen={setDetailsId} />
      )}

      <BookingFormDrawer open={formOpen} onOpenChange={setFormOpen} prefill={prefill} />
      <BookingDetailsDrawer bookingId={detailsId} open={!!detailsId} onOpenChange={(o) => !o && setDetailsId(null)} onEdit={() => {}} />
    </div>
  );
}

function DayGrid({ date, bookings, onOpen, onSlot, canCreate }) {
  const byHour = {};
  bookings.forEach((b) => { const h = parseInt(b.start_time.split(":")[0], 10); (byHour[h] = byHour[h] || []).push(b); });
  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="planning-day-grid">
      {HOURS.map((h) => (
        <div key={h} className="flex border-b border-slate-100 last:border-0">
          <div className="w-16 shrink-0 border-r border-slate-100 px-3 py-3 text-right font-mono text-xs text-slate-400">{String(h).padStart(2, "0")}:00</div>
          <div className="flex flex-1 flex-wrap gap-2 p-2">
            {(byHour[h] || []).map((b) => <BookingBlock key={b.id} b={b} onOpen={onOpen} />)}
            {canCreate && (byHour[h] || []).length === 0 && (
              <button onClick={() => onSlot(date, `${String(h).padStart(2, "0")}:00`)} data-testid={`planning-slot-${h}`}
                className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-slate-300 transition-colors hover:bg-slate-50 hover:text-primary"><Plus className="h-3.5 w-3.5" /></button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function WeekGrid({ start, bookings, onOpen }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const byDay = {};
  bookings.forEach((b) => { (byDay[b.booking_date] = byDay[b.booking_date] || []).push(b); });
  return (
    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7" data-testid="planning-week-grid">
      {days.map((d, i) => (
        <div key={d} className="rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-3 py-2"><p className="text-xs font-semibold text-slate-500">{DAYS_FR[i]}</p><p className="text-sm font-bold text-slate-900">{d.slice(8, 10)}/{d.slice(5, 7)}</p></div>
          <div className="max-h-[420px] space-y-1.5 overflow-y-auto p-2">
            {(byDay[d] || []).sort((a, b) => a.start_time.localeCompare(b.start_time)).map((b) => <BookingBlock key={b.id} b={b} onOpen={onOpen} compact />)}
            {!(byDay[d] || []).length && <p className="py-4 text-center text-xs text-slate-300">—</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function BookingBlock({ b, onOpen, compact }) {
  const s = BOOKING_STATUS[b.status];
  return (
    <button onClick={() => onOpen(b.id)} data-testid={`planning-booking-${b.id}`}
      className={`w-full rounded-lg border p-2 text-left transition-transform hover:scale-[1.01] ${s.cls} ${compact ? "" : "min-w-[180px] max-w-[240px]"}`}>
      <p className="font-mono text-xs font-bold">{b.start_time}–{b.end_time}</p>
      <p className="truncate text-xs font-semibold">{b.activity_name}</p>
      <p className="truncate text-[11px] opacity-80">{b.customer_name} · {b.participants_count}p</p>
      <div className="mt-1"><PaymentBadge status={b.payment_status} /></div>
    </button>
  );
}

const Stat = ({ label, value, mono }) => (
  <div><p className="text-xs uppercase tracking-wide text-slate-400">{label}</p><p className={`mt-0.5 text-lg font-bold text-slate-900 ${mono ? "font-mono" : ""}`}>{value}</p></div>
);
